import { lazy } from 'react';
import type { RouteObject } from 'react-router-dom';
import { Navigate } from 'react-router-dom';
import { ProtectedRoute } from '../components/feature/ProtectedRoute';
import AppLayout from '../components/feature/AppLayout';

// Lazy loading des pages
const HomePage = lazy(() => import('../pages/home/page'));
const LoginPage = lazy(() => import('../pages/login/page'));
const ForgotPasswordPage = lazy(() => import('../pages/forgot-password/page'));
const ResetPasswordPage = lazy(() => import('../pages/reset-password/page'));
const ForceChangePasswordPage = lazy(() => import('../pages/force-change-password/page'));
const DashboardPage = lazy(() => import('../pages/dashboard/page'));
const DossiersPage = lazy(() => import('../pages/dossiers/page'));
const NouveauDossierPage = lazy(() => import('../pages/nouveau-dossier/page'));
const DossierDetailPage = lazy(() => import('../pages/dossier-detail/page'));
const DemandesPage = lazy(() => import('../pages/demandes/page'));
const DemandeDetailPage = lazy(() => import('../pages/demande-detail/page'));
const PartiesPage = lazy(() => import('../pages/parties/page'));
const GestionDocumentairePage = lazy(() => import('../pages/gestion-documentaire/page'));
const NotificationsAlertesPage = lazy(() => import('../pages/notifications-alertes/page'));
const StatistiquesDelaisPage = lazy(() => import('../pages/statistiques-delais/page'));
const ConformiteOrganisationsPage = lazy(() => import('../pages/conformite-organisations/page'));
const SuiviDelais72hPage = lazy(() => import('../pages/suivi-delais-72h/page'));
const SuiviReponsesPage = lazy(() => import('../pages/suivi-reponses/page'));
const ReportingPage = lazy(() => import('../pages/reporting/page'));
const RechercheAvanceePage = lazy(() => import('../pages/recherche-avancee/page'));
const GestionUtilisateursPage = lazy(() => import('../pages/gestion-utilisateurs/page'));
const GestionOrganisationsPage = lazy(() => import('../pages/gestion-organisations/page'));
const AdministrationPage = lazy(() => import('../pages/administration/page'));
const ParametresPage = lazy(() => import('../pages/parametres/page'));
const ProfilPage = lazy(() => import('../pages/profil/page'));
const MonEspacePage = lazy(() => import('../pages/mon-espace/page'));
const SuperviseurPage = lazy(() => import('../pages/superviseur/page'));
const RapportsDisseminationPage = lazy(() => import('../pages/rapports-dissemination/page'));
const NouveauRapportPage = lazy(() => import('../pages/rapports-dissemination/nouveau/page'));
const StatsTypeOrganisationPage = lazy(() => import('../pages/stats-type-organisation/page'));
const EspaceEnqueteurPage = lazy(() => import('../pages/espace-enqueteur/page'));
const EspaceCollaborateurPage = lazy(() => import('../pages/espace-collaborateur/page'));
const EspaceLecteurPage = lazy(() => import('../pages/espace-lecteur/page'));
const EchangesInterOrganisationsPage = lazy(() => import('../pages/echanges-inter-organisations/page'));
const MonOrganismePage = lazy(() => import('../pages/mon-organisme/page'));
const DeclarationsSystematiquesPage = lazy(() => import('../pages/declarations-systematiques/page'));
const OperationsCentifPage = lazy(() => import('../pages/operation-centif/page'));
const StatistiquesAvanceesPage = lazy(() => import('../pages/statistiques-avancees/page'));
const NotFoundPage = lazy(() => import('../pages/NotFound'));
const GuideUtilisateurPage = lazy(() => import('../pages/guide-utilisateur/page'));
const NewslettersPage = lazy(() => import('../pages/newsletters/page'));
const routes: RouteObject[] = [
  {
    path: '/',
    element: <HomePage />,
  },
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/forgot-password',
    element: <ForgotPasswordPage />,
  },
  {
    path: '/reset-password',
    element: <ResetPasswordPage />,
  },
  {
    path: '/force-change-password',
    element: (
      <ProtectedRoute>
        <ForceChangePasswordPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/dashboard',
    element: (
      <ProtectedRoute>
        <AppLayout>
          <DashboardPage />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/mon-espace',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'superviseur', 'analyste', 'enqueteur']}>
        <AppLayout>
          <MonEspacePage />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/superviseur',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'superviseur']}>
        <AppLayout>
          <SuperviseurPage />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/espace-enqueteur',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'enqueteur']}>
        <AppLayout>
          <EspaceEnqueteurPage />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/espace-collaborateur',
    element: (
      <ProtectedRoute allowedRoles={['admin']}>
        <AppLayout>
          <EspaceCollaborateurPage />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/espace-lecteur',
    element: (
      <ProtectedRoute allowedRoles={['lecteur']}>
        <AppLayout>
          <EspaceLecteurPage />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/echanges-inter-organisations',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'superviseur', 'collaborateur_national', 'collaborateur_international']}>
        <AppLayout>
          <EchangesInterOrganisationsPage />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/dossiers',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'superviseur', 'analyste', 'agent_declarant', 'enqueteur', 'lecteur']}>
        <AppLayout>
          <DossiersPage />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/dossiers/nouveau',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'superviseur', 'analyste', 'agent_declarant']}>
        <AppLayout>
          <NouveauDossierPage />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/nouveau-dossier',
    element: <Navigate to="/dossiers/nouveau" replace />,
  },
  {
    path: '/dossiers/:id',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'superviseur', 'analyste', 'agent_declarant', 'enqueteur', 'lecteur']}>
        <AppLayout>
          <DossierDetailPage />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/demandes',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'superviseur', 'analyste', 'agent_declarant', 'lecteur']}>
        <AppLayout>
          <DemandesPage />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/demandes/:id',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'superviseur', 'analyste', 'agent_declarant', 'lecteur']}>
        <AppLayout>
          <DemandeDetailPage />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/parties',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'superviseur', 'analyste', 'agent_declarant']}>
        <AppLayout>
          <PartiesPage />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/gestion-documentaire',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'superviseur', 'analyste', 'agent_declarant', 'enqueteur', 'collaborateur_national', 'collaborateur_international']}>
        <AppLayout>
          <GestionDocumentairePage />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/notifications-alertes',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'superviseur', 'analyste', 'agent_declarant', 'enqueteur', 'collaborateur_national', 'collaborateur_international']}>
        <AppLayout>
          <NotificationsAlertesPage />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/statistiques-delais',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'superviseur']}>
        <AppLayout>
          <StatistiquesDelaisPage />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/stats-type-organisation',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'superviseur']}>
        <AppLayout>
          <StatsTypeOrganisationPage />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/conformite-organisations',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'superviseur']}>
        <AppLayout>
          <ConformiteOrganisationsPage />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/suivi-delais-72h',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'superviseur', 'analyste']}>
        <AppLayout>
          <SuiviDelais72hPage />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/suivi-reponses',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'superviseur', 'analyste', 'agent_declarant', 'collaborateur_national', 'collaborateur_international']}>
        <AppLayout>
          <SuiviReponsesPage />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/reporting',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'superviseur']}>
        <AppLayout>
          <ReportingPage />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/recherche-avancee',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'superviseur', 'analyste', 'agent_declarant']}>
        <AppLayout>
          <RechercheAvanceePage />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/gestion-utilisateurs',
    element: (
      <ProtectedRoute allowedRoles={['admin']}>
        <AppLayout>
          <GestionUtilisateursPage />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/gestion-organisations',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'superviseur']}>
        <AppLayout>
          <GestionOrganisationsPage />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/administration',
    element: (
      <ProtectedRoute allowedRoles={['admin']}>
        <AppLayout>
          <AdministrationPage />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/parametres',
    element: (
      <ProtectedRoute>
        <AppLayout>
          <ParametresPage />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/profil',
    element: (
      <ProtectedRoute>
        <AppLayout>
          <ProfilPage />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/rapports-dissemination',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'superviseur', 'analyste']}>
        <AppLayout>
          <RapportsDisseminationPage />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/rapports-dissemination/nouveau',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'superviseur', 'analyste']}>
        <AppLayout>
          <NouveauRapportPage />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/declarations-systematiques',
    element: (
      <ProtectedRoute allowedRoles={['admin']}>
        <AppLayout>
          <DeclarationsSystematiquesPage />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/operation-centif',
    element: (
      <ProtectedRoute allowedRoles={['admin']}>
        <AppLayout><OperationsCentifPage /></AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/mon-organisme',
    element: (
      <ProtectedRoute>
        <AppLayout>
          <MonOrganismePage />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/statistiques-avancees',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'superviseur']}>
        <AppLayout>
          <StatistiquesAvanceesPage />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/guide-utilisateur',
    element: (
      <ProtectedRoute>
        <AppLayout>
          <GuideUtilisateurPage />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/newsletters',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'superviseur']}>
        <AppLayout>
          <NewslettersPage />
        </AppLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '*',
    element: <NotFoundPage />,
  },
];

export default routes;